import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import classNames from 'classnames';
import Button from '@/shared/components/Button';
import intlShape from '@/shared/utils/intlShape';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import {
  AppointmentIcon,
  MailIcon,
  NoteIcon,
  PhoneIcon,
  ToDoIcon,
} from '@/shared/components/Icons';
import ContactIcon from '@/shared/components/ContactIcon';
import { injectStateService, StateService } from '@/shared/services/StateService';
import { CanCreate, CanUpdate } from '@/shared/components/security';
import { getRelationName } from '@/shared/components/RelationName';
import RelationTypeIcon from '@/shared/components/RelationTypeIcon';
import messages from './interactionMessages';
import TaskStatusToggle from '../tasks/TaskStatusToggle';
import ScrollableTable from '@/shared/components/table/ScrollableTable';
import ColumnHeader from '@/shared/components/table/ColumnHeader';
import moment from 'moment';
import FormattedDateShort from '@/shared/components/FormattedDateShort';
import DataCacheService from '@/shared/services/DataCacheService';
import classnames from 'classnames';

class RelationSummary extends Component {
  renderTodoRow = todo => {
    const { intl, showTaskDetailsModal, handleTasksUpdated } = this.props;
    const now = moment.utc();
    const deadlineUtc = moment.utc(todo.deadline);
    return (
      <tr key={todo.id}>
        <td>
          {todo.deadline ? (
            <FormattedDateShort
              className={classNames({
                orange: deadlineUtc.isAfter(now) && deadlineUtc.isBefore(now.add(1, 'weeks')),
                red: now.isAfter(deadlineUtc),
              })}
              value={todo.deadline}
            />
          ) : null}
        </td>
        <td>
          <div className="flex-row-nowrap">
            <TaskStatusToggle
              id={`TaskStatusToggle--${todo.id}`}
              value={{
                id: todo.id,
                status: todo.status,
                isBlocked: todo.isBlocked,
                inapplicable: todo.inapplicable,
                rejected: todo.rejected,
                taskLevel: todo.taskLevel,
              }}
              isMainTask
              onTasksUpdated={handleTasksUpdated}
              onOpenDetail={() => showTaskDetailsModal(todo.id)}
            />
            <span className="text-nowrap overflow-ellipsis flex-item">
              <a
                onClick={() => showTaskDetailsModal(todo.id)}
                title={intl.formatMessage(messages.OpenDetails)}
              >
                {todo.name}
              </a>
            </span>
          </div>
        </td>
      </tr>
    );
  };

  render() {
    const {
      intl,
      relation,
      onOpenCreateInteractionDialog,
      stateService,
      onSendEmail,
      onOpenCreateTodoDialog,
      todos,
      showTaskDetailsModal,
      loadMoreTodos,
      initialTodoRequestParams,
      showTodoList,
    } = this.props;

    const relationName = getRelationName(intl, relation);

    return (
      <div
        className={classNames('relation-summary-container', {
          'relation-summary': !showTodoList,
        })}
      >
        <div>
          <div className="margin20B row">
            <div
              className="col-xs-2 clickable"
              onClick={() => stateService.gotoRelation(relation.id)}
            >
              {!relation.eidPhotoUrl ? (
                <RelationTypeIcon
                  type={relation.type}
                  crm={relation.status}
                  className="notClickable pull-left"
                  style={{ fontSize: '46px' }}
                />
              ) : (
                <img src={relation.eidPhotoUrl} height="80px" />
              )}
            </div>
            <div className="col-xs-6">
              <div className="bold" style={{ marginBottom: '2px' }}>
                <span
                  className="clickable"
                  onClick={() => stateService.gotoRelation(relation.id)}
                  title={intl.formatMessage(messages.GoToRelation)}
                >
                  <span className="bold" style={{ wordBreak: 'break-all' }}>
                    {relationName}
                  </span>
                </span>
              </div>
              {relation && relation.alias && relation.alias !== relationName && (
                <div style={{ wordBreak: 'break-all' }}>{relation.alias}</div>
              )}
              <div>{relation.address}</div>
              <div>{relation.city}</div>
              <div>
                {relation.countryCode &&
                  intl.formatMessage(listItemMsg('country', relation.countryCode))}
              </div>
            </div>
          </div>

          {relation.type !== 2 && (
            <React.Fragment>
              {relation.enterpriseNumber && (
                <div className="row">
                  <div className="col-xs-4">
                    <FormattedMessage {...messages.EnterpriseNumber} />
                  </div>
                  <div className="col-xs-8 text-right">{relation.enterpriseNumber}</div>
                </div>
              )}

              {relation.vatNumber && (
                <div className="row">
                  <div className="col-xs-4">
                    <FormattedMessage {...messages.VatNumber} />
                  </div>
                  <div className="col-xs-8 text-right">{relation.vatNumber}</div>
                </div>
              )}
            </React.Fragment>
          )}
          <div className="row">
            <div className="col-xs-4">
              <FormattedMessage {...messages.PhoneNumber} />
            </div>
            <div className="col-xs-8 text-right break-word">
              <ContactIcon type="phone" value={relation.phone || relation.mobile} />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-4">
              <FormattedMessage {...messages.Email} />
            </div>
            <div className="col-xs-8 text-right break-word">
              <ContactIcon type="email" value={relation.email} />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-4">
              <FormattedMessage {...messages.Type} />
            </div>
            <div className="col-xs-8 text-right">
              <FormattedListValue list="relationtype" id={relation.type} />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-4">
              <FormattedMessage {...messages.Status} />
            </div>
            <div className="col-xs-8 text-right">
              <FormattedListValue list="crmtype" id={relation.status} />
            </div>
          </div>
          {relation.type !== 2 && relation.sector && (
            <div className="row">
              <div className="col-xs-4">
                <FormattedMessage {...messages.Sector} />
              </div>
              <div className="col-xs-8 text-right">
                {relation.sector !== null && (
                  <FormattedListValue list="sector" id={relation.sector} />
                )}
              </div>
            </div>
          )}
          {!onOpenCreateInteractionDialog && !onSendEmail ? null : (
            <div className="margin20T">
              <CanCreate on="InteractionsOwn">
                {!!onOpenCreateInteractionDialog && (
                  <Fragment>
                    <Button
                      btnClassName="btn-block"
                      onClick={() => onOpenCreateInteractionDialog(0)}
                    >
                      <NoteIcon />
                      <span className="margin5L">
                        <FormattedMessage {...messages.NewNote} />
                      </span>
                    </Button>
                    <Button
                      btnClassName="btn-block"
                      onClick={() => onOpenCreateInteractionDialog(3)}
                    >
                      <AppointmentIcon />
                      <span className="margin5L">
                        <FormattedMessage {...messages.NewAppointment} />
                      </span>
                    </Button>
                    <Button
                      btnClassName="btn-block"
                      onClick={() => onOpenCreateInteractionDialog(1)}
                    >
                      <PhoneIcon />
                      <span className="margin5L">
                        <FormattedMessage {...messages.NewPhoneCall} />
                      </span>
                    </Button>
                  </Fragment>
                )}
              </CanCreate>
              <CanCreate on="InteractionsOwn">
                {!!onSendEmail && (
                  <Button btnClassName="btn-block" onClick={() => onSendEmail()}>
                    <MailIcon />
                    <span className="margin5L">
                      <FormattedMessage {...messages.SendEmail} />
                    </span>
                  </Button>
                )}
              </CanCreate>
              <CanCreate on="Todo">
                {!!onOpenCreateTodoDialog && (
                  <Button btnClassName="btn-block" onClick={() => onOpenCreateTodoDialog()}>
                    <ToDoIcon />
                    <span className="margin5L">
                      <FormattedMessage {...messages.CreateNewToDo} />
                    </span>
                  </Button>
                )}
              </CanCreate>
            </div>
          )}
        </div>

        {showTodoList ? (
          <>
            <div className="bold text-center" style={{ marginTop: '20px', marginBottom: '5px' }}>
              <FormattedMessage {...messages.ToDos} />
            </div>
            <ScrollableTable
              tableClassName="table-grid relation-summary-todo-table"
              items={todos}
              useWindow={false}
              renderRow={this.renderTodoRow}
              renderTBody
              loadItems={loadMoreTodos}
              initialSearchParams={initialTodoRequestParams}
            >
              <ColumnHeader title={intl.formatMessage(messages.Deadline)} />
              <ColumnHeader title={intl.formatMessage(messages.Description)} />
            </ScrollableTable>
          </>
        ) : (
          <>
            <div className="row hidden-xs">
              <div
                className="bold col-md-12 text-center"
                style={{ marginTop: '20px', marginBottom: '5px' }}
              >
                <FormattedMessage {...messages.Contacts} />
              </div>
            </div>
            {!relation.linkedRelations.length && (
              <div className="row">
                <div className="col-md-12 text-center margin10T" style={{ marginBottom: '5px' }}>
                  {!relation.linkedRelations.length && (
                    <FormattedMessage {...messages.NoContactsPresentForRelation} />
                  )}
                </div>
              </div>
            )}
            {relation.linkedRelations.map((linkedRelation, i) => {
              const isBorderNeeded = i !== relation.linkedRelations.length - 1;
              return (
                <div
                  key={linkedRelation.relationId}
                  className="row margin5T hidden-xs"
                  style={isBorderNeeded ? { borderBottom: '1px solid #8080803d' } : {}}
                >
                  <div className="col-xs-4">
                    <p>
                      <span
                        className="clickable"
                        onClick={() => stateService.gotoRelation(linkedRelation.relationId)}
                        title={intl.formatMessage(messages.GoToRelation)}
                      >
                        <span className="bold">{linkedRelation.relationName}</span>
                      </span>
                    </p>
                    <p>
                      {linkedRelation.linkTypes
                        .map(x => intl.formatMessage(listItemMsg('linktypeto', x)))
                        .join(', ')}
                    </p>
                  </div>
                  <div className="col-xs-8 text-right">
                    {linkedRelation.phone && (
                      <p className="break-word">
                        <ContactIcon type="phone" value={linkedRelation.phone} />
                      </p>
                    )}
                    {linkedRelation.mobile && !linkedRelation.phone && (
                      <p className="break-word">
                        <ContactIcon type="phone" value={linkedRelation.mobile} />
                      </p>
                    )}
                    {linkedRelation.email && (
                      <p className="break-word">
                        <ContactIcon type="email" value={linkedRelation.email} />
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  }
}

RelationSummary.propTypes = {
  stateService: PropTypes.instanceOf(StateService).isRequired,
  intl: intlShape.isRequired,
  relation: PropTypes.shape({}).isRequired,
  onOpenCreateInteractionDialog: PropTypes.func,
  onSendEmail: PropTypes.func,
  onOpenCreateTodoDialog: PropTypes.func,
};
RelationSummary.defaultProps = {
  onOpenCreateInteractionDialog: null,
  onSendEmail: null,
  onOpenCreateTodoDialog: null,
};

export default injectStateService(injectIntl(RelationSummary));
